/**
 * Unequal (Futoshiki) - Type Definitions
 *
 * Based on the gameplay documentation in apps/unequal/docs/unequal-play.md
 */

/**
 * Game mode - determines constraint type
 */
export enum GameMode {
  UNEQUAL = 0, // Greater-than constraints (>)
  ADJACENT = 1, // Consecutive number constraints (|, -)
}

/**
 * Difficulty levels
 */
export enum Difficulty {
  LATIN = 0, // Trivial - only Latin square logic
  EASY = 1, // Basic constraint propagation
  SET = 2, // Set-based reasoning (Tricky)
  EXTREME = 3, // Advanced deduction
  RECURSIVE = 4, // Requires guessing/backtracking
}

/**
 * Direction constants
 */
export enum Direction {
  UP = 0,
  RIGHT = 1,
  DOWN = 2,
  LEFT = 3,
}

/**
 * Cell flags (bitwise)
 */
export const CellFlags = {
  IMMUTABLE: 0x0001, // Given clue (cannot be edited)
  ADJ_UP: 0x0002, // Adjacency/inequality pointing up
  ADJ_RIGHT: 0x0004, // Adjacency/inequality pointing right
  ADJ_DOWN: 0x0008, // Adjacency/inequality pointing down
  ADJ_LEFT: 0x0010, // Adjacency/inequality pointing left
  ERROR: 0x0020, // Cell contains error (duplicate in row/col)
  ERROR_UP: 0x0040, // Error in upward constraint
  ERROR_RIGHT: 0x0080, // Error in rightward constraint
  ERROR_DOWN: 0x0100, // Error in downward constraint
  ERROR_LEFT: 0x0200, // Error in leftward constraint
  SPENT_UP: 0x0400, // User marked up constraint as "solved"
  SPENT_RIGHT: 0x0800, // User marked right constraint as "solved"
  SPENT_DOWN: 0x1000, // User marked down constraint as "solved"
  SPENT_LEFT: 0x2000, // User marked left constraint as "solved"
} as const;

/**
 * Direction info for constraint checking
 */
export interface DirectionInfo {
  flag: number; // Flag for this direction
  oppositeFlag: number; // Flag for opposite direction
  errorFlag: number; // Error flag for this direction
  spentFlag: number; // Spent flag for this direction
  dx: number; // X offset
  dy: number; // Y offset
  char: string; // Display character for UNEQUAL mode
  adjChar: string; // Display character for ADJACENT mode
}

/**
 * Direction descriptors
 */
export const DIRECTIONS: DirectionInfo[] = [
  {
    flag: CellFlags.ADJ_UP,
    oppositeFlag: CellFlags.ADJ_DOWN,
    errorFlag: CellFlags.ERROR_UP,
    spentFlag: CellFlags.SPENT_UP,
    dx: 0,
    dy: -1,
    char: '^',
    adjChar: '-',
  },
  {
    flag: CellFlags.ADJ_RIGHT,
    oppositeFlag: CellFlags.ADJ_LEFT,
    errorFlag: CellFlags.ERROR_RIGHT,
    spentFlag: CellFlags.SPENT_RIGHT,
    dx: 1,
    dy: 0,
    char: '>',
    adjChar: '|',
  },
  {
    flag: CellFlags.ADJ_DOWN,
    oppositeFlag: CellFlags.ADJ_UP,
    errorFlag: CellFlags.ERROR_DOWN,
    spentFlag: CellFlags.SPENT_DOWN,
    dx: 0,
    dy: 1,
    char: 'v',
    adjChar: '-',
  },
  {
    flag: CellFlags.ADJ_LEFT,
    oppositeFlag: CellFlags.ADJ_RIGHT,
    errorFlag: CellFlags.ERROR_LEFT,
    spentFlag: CellFlags.SPENT_LEFT,
    dx: -1,
    dy: 0,
    char: '<',
    adjChar: '|',
  },
];

/**
 * Game parameters
 */
export interface GameParameters {
  order: number; // Grid size (3-32, typically 4-7)
  difficulty: Difficulty;
  mode: GameMode;
}

/**
 * Game state
 */
export interface GameState {
  order: number; // Grid size N
  mode: GameMode;
  completed: boolean; // True if puzzle solved
  cheated: boolean; // True if user used solver/hint

  // Grid data
  nums: number[]; // Cell values: 0 = empty, 1-N = filled (size order×order)
  hints: boolean[]; // Pencil marks (size order×order×order)
  flags: number[]; // Cell constraint flags (size order×order)

  // Solution for hints/validation
  solution: number[];

  // Selection state
  selectedX: number;
  selectedY: number;
}

/**
 * Move types
 */
export enum MoveType {
  FILL_CELL = 'R', // R<x>,<y>,<n>
  TOGGLE_PENCIL = 'P', // P<x>,<y>,<n>
  FILL_ALL_PENCILS = 'M', // M
  GET_HINT = 'H', // H
  SOLVE = 'S', // S<solution>
  TOGGLE_SPENT = 'F', // F<x>,<y>,<flags>
}

/**
 * Puzzle preset
 */
export interface PuzzlePreset {
  name: string;
  order: number;
  difficulty: Difficulty;
  mode: GameMode;
}

/**
 * Standard presets
 */
export const PRESETS: PuzzlePreset[] = [
  { name: '4×4 Easy', order: 4, difficulty: Difficulty.EASY, mode: GameMode.UNEQUAL },
  { name: '5×5 Easy', order: 5, difficulty: Difficulty.EASY, mode: GameMode.UNEQUAL },
  { name: '5×5 Tricky', order: 5, difficulty: Difficulty.SET, mode: GameMode.UNEQUAL },
  { name: '5×5 Tricky Adjacent', order: 5, difficulty: Difficulty.SET, mode: GameMode.ADJACENT },
  { name: '5×5 Extreme', order: 5, difficulty: Difficulty.EXTREME, mode: GameMode.UNEQUAL },
  { name: '5×5 Extreme Adjacent', order: 5, difficulty: Difficulty.EXTREME, mode: GameMode.ADJACENT },
  { name: '6×6 Tricky', order: 6, difficulty: Difficulty.SET, mode: GameMode.UNEQUAL },
  { name: '7×7 Tricky', order: 7, difficulty: Difficulty.SET, mode: GameMode.UNEQUAL },
];

/**
 * Check result
 */
export enum CheckResult {
  INVALID = -1, // Contains errors
  INCOMPLETE = 0, // Has empty cells
  COMPLETE = 1, // Complete and correct
}

/**
 * Clue type encoding
 */
export const ClueType = {
  NUMBER: 4,
  UP: 0,
  RIGHT: 1,
  DOWN: 2,
  LEFT: 3,
} as const;
